import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { AlertTriangle, CheckCircle, CreditCard, Wifi, Monitor, Server, Trash2, CheckCheck, RefreshCw, ChevronDown, ChevronUp, ExternalLink, User, Bell, Download } from 'lucide-react';

const CAT_CONFIG = {
  api_error: { label: 'API Error', color: 'bg-red-100 text-red-800', icon: Server },
  payment_failure: { label: 'Payment Failure', color: 'bg-orange-100 text-orange-800', icon: CreditCard },
  notification_failure: { label: 'Notification Failure', color: 'bg-amber-100 text-amber-800', icon: Wifi },
  system_error: { label: 'System Error', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  frontend_exception: { label: 'Frontend Error', color: 'bg-purple-100 text-purple-800', icon: Monitor },
};

const AdminErrorLog = () => {
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState({ total: 0, unresolved: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterResolved, setFilterResolved] = useState('false');
  const [filterFrom, setFilterFrom] = useState('');   // YYYY-MM-DD
  const [filterTo, setFilterTo] = useState('');       // YYYY-MM-DD
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [expanded, setExpanded] = useState({}); // err.id → bool

  // Best-effort UA → "Browser / OS" label so admins see "Chrome / iOS" without
  // reading a 200-char user-agent string.
  const parseUA = (ua) => {
    if (!ua) return '';
    const browser = /Edg\//.test(ua) ? 'Edge' :
      /Chrome\//.test(ua) && !/Chromium\//.test(ua) ? 'Chrome' :
      /Firefox\//.test(ua) ? 'Firefox' :
      /Safari\//.test(ua) && !/Chrome\//.test(ua) ? 'Safari' : 'Browser';
    const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
      /Android/.test(ua) ? 'Android' :
      /Windows/.test(ua) ? 'Windows' :
      /Mac OS X/.test(ua) ? 'macOS' :
      /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} · ${os}` : browser;
  };

  // Frontend exceptions pack stack + componentStack + ua into `details`.
  // Pull the UA back out so we can render a friendly "Chrome / iOS" pill.
  const extractUA = (details) => {
    if (!details) return '';
    const m = details.match(/--- ua ---\s*\n([^\n]+)/);
    return m ? m[1].trim() : '';
  };

  useEffect(() => { fetchErrors(); }, [filterCat, filterResolved, filterFrom, filterTo]);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set('category', filterCat);
      if (filterResolved) params.set('resolved', filterResolved);
      if (filterFrom) params.set('date_from', filterFrom);
      if (filterTo) params.set('date_to', filterTo);
      params.set('limit', '100');
      const res = await axiosInstance.get(`/admin/errors?${params.toString()}`);
      setErrors(res.data.errors || []);
      setSummary(res.data.summary || { total: 0, unresolved: 0, byCategory: {} });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load errors' });
    } finally {
      setLoading(false);
    }
  };

  // CSV export — downloads ALL rows matching the active filters (not capped
  // at the 100 visible). The browser receives a streaming text/csv response
  // from /api/admin/errors/export and saves it as find2day-error-log-YYYY-MM-DD.csv.
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set('category', filterCat);
      if (filterResolved) params.set('resolved', filterResolved);
      if (filterFrom) params.set('date_from', filterFrom);
      if (filterTo) params.set('date_to', filterTo);
      const res = await axiosInstance.get(`/admin/errors/export?${params.toString()}`, {
        responseType: 'blob',
      });
      // Pull filename + row count out of headers when available
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `find2day-error-log-${new Date().toISOString().slice(0, 10)}.csv`;
      const rowCount = res.headers['x-row-count'] || '0';

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      flash('success', `Exported ${rowCount} row${rowCount === '1' ? '' : 's'} to ${filename}`);
    } catch (err) {
      flash('error', 'Export failed — see console');
      // eslint-disable-next-line no-console
      console.error('[error-log-export] failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilterCat('');
    setFilterResolved('false');
    setFilterFrom('');
    setFilterTo('');
  };

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const resolveError = async (id) => {
    try {
      await axiosInstance.put(`/admin/errors/${id}/resolve`);
      flash('success', 'Error resolved');
      fetchErrors();
    } catch (err) {
      flash('error', 'Failed to resolve');
    }
  };

  const resolveAll = async () => {
    if (!window.confirm('Mark all unresolved errors as resolved?')) return;
    try {
      const res = await axiosInstance.post('/admin/errors/resolve-all');
      flash('success', res.data.message);
      fetchErrors();
    } catch (err) {
      flash('error', 'Failed');
    }
  };

  const clearResolved = async () => {
    if (!window.confirm('Delete all resolved errors permanently?')) return;
    try {
      const res = await axiosInstance.delete('/admin/errors/clear');
      flash('success', res.data.message);
      fetchErrors();
    } catch (err) {
      flash('error', 'Failed');
    }
  };

  // One-shot maintenance utility — link existing device subscriptions to user
  // accounts by matching their stored email. Idempotent. Useful right after
  // deploying the userId-linking change to retroactively make the bell work
  // for users who subscribed before the fix.
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null); // { ok, text, detail }

  const runNotificationBackfill = async () => {
    if (backfillRunning) return;
    if (!window.confirm('Run notification backfill now? This links existing device subscriptions to user accounts so push/bell notifications can reach them.')) return;

    setBackfillRunning(true);
    setBackfillResult({ ok: null, text: 'Running backfill…', detail: '' });
    // Verbose console trace so the user can verify the request from DevTools.
    // eslint-disable-next-line no-console
    console.log('[backfill] POST /api/admin/backfill-userid-on-subs');
    try {
      const res = await axiosInstance.post('/admin/backfill-userid-on-subs');
      // eslint-disable-next-line no-console
      console.log('[backfill] response:', res.status, res.data);
      const linked = res.data?.linked ?? 0;
      const examined = res.data?.examined ?? 0;
      setBackfillResult({
        ok: true,
        text: `Backfill complete: ${linked} linked, ${examined} examined.`,
        detail: linked === 0 && examined === 0
          ? 'No subscriptions had an email field to match — that is normal if users have only ever subscribed via the heart icon (which writes to db.favorites instead, and is already handled by the notification fan-out).'
          : '',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[backfill] failed:', err);
      const status = err.response?.status || 'network';
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      setBackfillResult({
        ok: false,
        text: `Backfill failed (HTTP ${status})`,
        detail: typeof detail === 'string' ? detail : JSON.stringify(detail),
      });
    } finally {
      setBackfillRunning(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="error-log-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 font-medium uppercase">Total Errors</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="total-errors">{summary.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 font-medium uppercase">Unresolved</p>
          <p className="text-2xl font-bold text-red-600" data-testid="unresolved-errors">{summary.unresolved}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4 col-span-2 md:col-span-1">
          <p className="text-xs text-gray-500 font-medium uppercase mb-2">By Category</p>
          <div className="space-y-1">
            {Object.entries(CAT_CONFIG).map(([key, cfg]) => {
              const count = summary.byCategory?.[key] || 0;
              if (count === 0) return null;
              return (
                <div key={key} className="flex items-center justify-between text-xs">
                  <span className={`px-1.5 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                  <span className="font-bold">{count}</span>
                </div>
              );
            })}
            {Object.values(summary.byCategory || {}).every(v => v === 0) && (
              <p className="text-xs text-green-600 font-medium">All clear</p>
            )}
          </div>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Maintenance — admin-only one-shot utilities */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3" data-testid="maintenance-panel">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
              <Bell className="h-4 w-4" /> Notification backfill
            </p>
            <p className="text-xs text-purple-700 mt-0.5">
              Links existing device subscriptions to user accounts by email so the bell + push notifications reach users who subscribed before the userId-linking change. Idempotent — safe to run multiple times.
            </p>
          </div>
          <button
            onClick={runNotificationBackfill}
            disabled={backfillRunning}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60 disabled:cursor-wait whitespace-nowrap"
            data-testid="run-notification-backfill-btn"
          >
            {backfillRunning ? <><RefreshCw className="h-4 w-4 animate-spin" /> Running…</> : <><Bell className="h-4 w-4" /> Run notification backfill</>}
          </button>
        </div>

        {/* Inline result block — sticks until next run or page refresh */}
        {backfillResult && (
          <div
            data-testid="backfill-result"
            className={`rounded-lg p-3 text-sm border ${
              backfillResult.ok === true ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
              backfillResult.ok === false ? 'bg-red-50 border-red-200 text-red-800' :
              'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="font-medium">{backfillResult.text}</p>
                {backfillResult.detail && (
                  <p className="text-xs opacity-80 mt-1 break-words">{backfillResult.detail}</p>
                )}
              </div>
              <button
                onClick={() => setBackfillResult(null)}
                className="text-current opacity-50 hover:opacity-100"
                data-testid="dismiss-backfill-result"
                aria-label="Dismiss"
              >×</button>
            </div>
          </div>
        )}
      </div>

      {/* Filters + Actions */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Category</label>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="error-cat-filter">
            <option value="">All categories</option>
            {Object.entries(CAT_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Status</label>
          <select value={filterResolved} onChange={e => setFilterResolved(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="error-resolved-filter">
            <option value="">All</option>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
            data-testid="error-from-filter"
            max={filterTo || undefined}
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
            data-testid="error-to-filter"
            min={filterFrom || undefined}
          />
        </div>
        {(filterFrom || filterTo || filterCat || filterResolved !== 'false') && (
          <button
            onClick={resetFilters}
            className="px-3 py-2 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="error-reset-filters-btn"
          >
            Reset
          </button>
        )}
        <button onClick={fetchErrors} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200" data-testid="refresh-errors-btn" title="Refresh">
          <RefreshCw className="h-4 w-4 text-gray-600" />
        </button>
        <div className="ml-auto flex gap-2 items-end">
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-60 disabled:cursor-wait"
            data-testid="export-errors-csv-btn"
            title="Download all rows matching the current filters as CSV"
          >
            {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button onClick={resolveAll} className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100" data-testid="resolve-all-btn">
            <CheckCheck className="h-4 w-4" /> Resolve All
          </button>
          <button onClick={clearResolved} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-xs font-medium hover:bg-gray-100" data-testid="clear-resolved-btn">
            <Trash2 className="h-4 w-4" /> Clear Resolved
          </button>
        </div>
      </div>

      {/* Error List */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">Loading...</div>
      ) : errors.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <CheckCircle className="h-10 w-10 text-green-300 mx-auto mb-2" />
          <p className="text-gray-500">No errors found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {errors.map(err => {
            const cfg = CAT_CONFIG[err.category] || CAT_CONFIG.system_error;
            const Icon = cfg.icon;
            const ua = extractUA(err.details);
            const browser = parseUA(ua);
            const isExpanded = !!expanded[err.id];
            return (
              <div key={err.id} className={`bg-white rounded-lg shadow-sm border p-4 ${err.resolved ? 'opacity-60' : ''}`} data-testid={`error-row-${err.id}`}>
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                      {err.statusCode > 0 && <span className="text-xs text-gray-500 font-mono">HTTP {err.statusCode}</span>}
                      {err.endpoint && (
                        err.endpoint.startsWith('http') ? (
                          <a href={err.endpoint} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline font-mono truncate max-w-[280px] inline-flex items-center gap-1" title={err.endpoint}>
                            <ExternalLink className="h-3 w-3" />{err.endpoint.replace(/^https?:\/\/[^/]+/, '')}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400 font-mono truncate max-w-[280px]">{err.endpoint}</span>
                        )
                      )}
                      {err.userId && (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 inline-flex items-center gap-1" title={err.userId}>
                          <User className="h-3 w-3" />{err.userId.slice(0, 8)}
                        </span>
                      )}
                      {browser && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{browser}</span>}
                      {err.resolved && <span className="text-xs text-green-600 font-medium">Resolved</span>}
                    </div>
                    <p className="text-sm text-gray-900 font-medium break-words">{err.message}</p>
                    {err.details && (
                      <>
                        <button
                          onClick={() => setExpanded(s => ({ ...s, [err.id]: !s[err.id] }))}
                          className="text-[11px] text-purple-600 hover:text-purple-800 mt-1 inline-flex items-center gap-0.5"
                          data-testid={`error-toggle-details-${err.id}`}
                        >
                          {isExpanded ? <><ChevronUp className="h-3 w-3" /> Hide stack</> : <><ChevronDown className="h-3 w-3" /> Show stack</>}
                        </button>
                        {isExpanded ? (
                          <pre className="text-[11px] text-gray-600 mt-1 font-mono whitespace-pre-wrap break-all bg-gray-50 rounded p-2 max-h-72 overflow-auto" data-testid={`error-details-${err.id}`}>{err.details}</pre>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 font-mono">{err.details}</p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-400">{formatDate(err.createdAt)}</span>
                    {!err.resolved && (
                      <button onClick={() => resolveError(err.id)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Mark resolved" data-testid={`resolve-error-${err.id}`}>
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminErrorLog;
